// Wishlist. Destinations saved from the Cari deck, private to their owner.
//
// Every card is the same shape by construction: one fixed height photo, a
// one line name, one budget line, and a footer pinned to the same baseline
// with margin-top:auto, so a missing estimate or a long name can never make
// one card taller than its neighbour.
import { useEffect, useMemo, useState } from "react";
import { Icon, IconBookmark, IconCheck, IconMap, IconPlan, IconRoute, IconTrash } from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { apiClient } from "../lib/api";
import { formatMoney } from "../lib/format";
import { navigate } from "../lib/router";
import { useApp } from "../state/app";

function todayInput(): string {
  const now = new Date();
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function WishlistScreen() {
  const {
    t,
    locale,
    wishlist,
    refreshWishlist,
    removeFromWishlist,
    createTrip,
    setActiveTripId,
    setItineraryDraft,
    focusOnMap,
  } = useApp();
  const [selected, setSelected] = useState<string[]>([]);
  const [state, setState] = useState<"idle" | "working" | "failed">("idle");

  useEffect(() => {
    void refreshWishlist();
  }, [refreshWishlist]);

  const chosen = useMemo(
    () => wishlist.filter((item) => selected.includes(item.placeId)),
    [wishlist, selected],
  );

  function toggle(placeId: string): void {
    setSelected((current) =>
      current.includes(placeId)
        ? current.filter((id) => id !== placeId)
        : [...current, placeId],
    );
  }

  async function makeTrip(): Promise<void> {
    if (chosen.length === 0) return;
    setState("working");
    try {
      // Wired to the existing trips module rather than a parallel planning
      // model, exactly as the contract asks.
      const trip = await createTrip({
        title: chosen.map((item) => item.name).join(", ").slice(0, 120),
        startDate: todayInput(),
        description: chosen.map((item) => item.name).join(", "),
      });
      setActiveTripId(trip.id);
      setSelected([]);
      setState("idle");
      navigate("trips");
    } catch {
      setState("failed");
    }
  }

  if (wishlist.length === 0) {
    return (
      <div className="screen-pad">
        <EmptyState
          glyph={<IconBookmark />}
          title={t("wishlist.empty")}
          hint={t("wishlist.emptyHint")}
          action={
            <button type="button" className="btn btn-quiet" onClick={() => navigate("cari")}>
              {t("wishlist.openDiscover")}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="screen-pad">
      <p className="section-sub">{t("wishlist.lede")}</p>

      <div className="wish-grid">
        {wishlist.map((item) => {
          const media = item.media[0];
          const picked = selected.includes(item.placeId);
          return (
            <article className="wish-card" key={item.placeId}>
              <div className="wish-media">
                {media ? (
                  media.mime.startsWith("video/") ? (
                    <video className="wish-media-item" src={apiClient.mediaUrl(media.storageKey)} muted />
                  ) : (
                    <img className="wish-media-item" src={apiClient.mediaUrl(media.storageKey)} alt="" />
                  )
                ) : null}
              </div>
              <h2 className="wish-name">{item.name}</h2>
              <p className="wish-budget">
                {item.budget
                  ? `${formatMoney(item.budget.currency, item.budget.min, locale)} ${t("trips.until")} ${formatMoney(item.budget.currency, item.budget.max, locale)}`
                  : t("budget.noEstimate")}
              </p>
              <div className="wish-foot">
                <button
                  type="button"
                  className={picked ? "btn btn-quiet btn-on" : "btn btn-quiet"}
                  aria-pressed={picked}
                  onClick={() => toggle(item.placeId)}
                >
                  {picked ? (
                    <Icon size={16}>
                      <IconCheck />
                    </Icon>
                  ) : null}
                  {t("wishlist.select")}
                </button>
                <div className="wish-actions">
                <button
                  type="button"
                  className="btn-icon"
                  aria-label={t("wishlist.toItinerary")}
                  onClick={() => {
                    // Handed to the itinerary through the app store, which
                    // prefills its add form. One model, no parallel planner.
                    setItineraryDraft({
                      placeId: item.placeId,
                      placeName: item.name,
                      lat: item.lat,
                      lng: item.lng,
                    });
                    navigate("itinerary");
                  }}
                >
                  <Icon size={18}>
                    <IconPlan />
                  </Icon>
                </button>
                <button
                  type="button"
                  className="btn-icon"
                  aria-label={t("map.placeTitle")}
                  onClick={() => {
                    focusOnMap({ lat: item.lat, lng: item.lng });
                    navigate("map");
                  }}
                >
                  <Icon size={18}>
                    <IconMap />
                  </Icon>
                </button>
                <button
                  type="button"
                  className="btn-icon"
                  aria-label={t("wishlist.remove")}
                  onClick={() => void removeFromWishlist(item.placeId)}
                >
                  <Icon size={18}>
                    <IconTrash />
                  </Icon>
                </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={chosen.length === 0 || state === "working"}
          onClick={() => void makeTrip()}
        >
          <Icon size={18} color="var(--color-primary-contrast)">
            <IconRoute />
          </Icon>
          {t("wishlist.makeTrip")}
        </button>
        <span className="field-note">
          {chosen.length} {t("wishlist.selected")}
        </span>
      </div>
      {state === "failed" ? <p className="field-note">{t("wishlist.tripFailed")}</p> : null}
      <p className="field-note">{t("discover.budgetDummy")}</p>
    </div>
  );
}
